import React, { useEffect, useState, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import api from '../../api/client';
import Button from '../../components/Button';
import Badge from '../../components/Badge';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [false, 1, 2, 3] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

export default function AdminNewsletter() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [recipients, setRecipients] = useState([]);
  const [selected, setSelected] = useState({}); // ticket_id -> bool
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [filterUnpaidOnly, setFilterUnpaidOnly] = useState(false);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const [preview, setPreview] = useState(null); // html string
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState('');

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [viewingEntry, setViewingEntry] = useState(null); // history entry being previewed

  const loadHistory = () => {
    setHistoryLoading(true);
    api.get('/api/admin/newsletter/history').then((r) => setHistory(r.data)).finally(() => setHistoryLoading(false));
  };

  useEffect(() => { api.get('/api/events/all').then((r) => setEvents(r.data)); loadHistory(); }, []);

  const eventName = (eventId) => events.find((e) => e.id === eventId)?.name || `Event #${eventId}`;

  function fmtDate(d) {
    return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  useEffect(() => {
    if (!selectedEvent) { setRecipients([]); setSelected({}); return; }
    setRecipientsLoading(true);
    setResult('');
    api.get(`/api/admin/newsletter/recipients?event_id=${selectedEvent}`)
      .then((r) => {
        setRecipients(r.data);
        const sel = {};
        r.data.forEach((rec) => { sel[rec.ticket_id] = true; });
        setSelected(sel);
      })
      .finally(() => setRecipientsLoading(false));
  }, [selectedEvent]);

  const visibleRecipients = useMemo(
    () => (filterUnpaidOnly ? recipients.filter((r) => r.payment_status !== 'confirmed') : recipients),
    [recipients, filterUnpaidOnly]
  );

  const toggle = (ticketId) => setSelected((prev) => ({ ...prev, [ticketId]: !prev[ticketId] }));
  const toggleAll = (checked) => {
    const sel = {};
    visibleRecipients.forEach((r) => { sel[r.ticket_id] = checked; });
    setSelected((prev) => ({ ...prev, ...sel }));
  };

  const selectedIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => parseInt(k, 10));
  const unpaidCount = recipients.filter((r) => r.payment_status !== 'confirmed').length;

  const [deletingId, setDeletingId] = useState(null);

  const deleteEntry = async (entry) => {
    if (!window.confirm(`Newsletter "${entry.subject}" wirklich aus der Liste löschen? Die Mail wurde bereits verschickt, das löscht nur den Verlaufseintrag.`)) return;
    setDeletingId(entry.id);
    try {
      await api.delete(`/api/admin/newsletter/history/${entry.id}`);
      setHistory((prev) => prev.filter((h) => h.id !== entry.id));
      if (viewingEntry?.id === entry.id) setViewingEntry(null);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler beim Löschen'); }
    finally { setDeletingId(null); }
  };

  const viewEntry = async (entry) => {
    try {
      const r = await api.post('/api/admin/newsletter/preview', { subject: entry.subject, body_html: entry.body_html });
      setViewingEntry({ ...entry, html: r.data.html });
    } catch (err) { alert(err.response?.data?.detail || 'Fehler beim Laden'); }
  };

  const openPreview = async () => {
    if (!subject.trim() || !body.trim()) { alert('Bitte Betreff und Inhalt ausfüllen.'); return; }
    setPreviewLoading(true);
    try {
      const r = await api.post('/api/admin/newsletter/preview', { subject, body_html: body });
      setPreview(r.data.html);
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setPreviewLoading(false); }
  };

  const send = async () => {
    if (!selectedEvent || selectedIds.length === 0) return;
    if (!window.confirm(`Newsletter an ${selectedIds.length} Empfänger senden?`)) return;
    setSending(true);
    try {
      const r = await api.post('/api/admin/newsletter/send', {
        event_id: parseInt(selectedEvent, 10),
        subject,
        body_html: body,
        ticket_ids: selectedIds,
      });
      setResult(`✅ Newsletter an ${r.data.sent} Empfänger gesendet.`);
      setPreview(null);
      loadHistory();
    } catch (err) { alert(err.response?.data?.detail || 'Fehler'); }
    finally { setSending(false); }
  };

  return (
    <div>
      <h2 className="page-title">Newsletter</h2>

      <div className="card mb-4">
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Event</label>
        <select className="input w-full max-w-md" value={selectedEvent} onChange={(e) => setSelectedEvent(e.target.value)}>
          <option value="">Event auswählen…</option>
          {events.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      {selectedEvent && (
        <div className="card mb-4">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div>
              <h3 className="font-bold text-ocean-800">Empfänger</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {recipients.length} für dieses Event angemeldet
                {unpaidCount > 0 && <span className="text-orange-600 font-medium"> · {unpaidCount} noch nicht bezahlt</span>}
              </p>
            </div>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 cursor-pointer">
              <input type="checkbox" checked={filterUnpaidOnly} onChange={(e) => setFilterUnpaidOnly(e.target.checked)} />
              Nur nicht bezahlte anzeigen
            </label>
          </div>

          {recipientsLoading ? (
            <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" /></div>
          ) : (
            <>
              <div className="flex gap-3 mb-2 text-xs">
                <button className="text-ocean-600 font-medium hover:underline" onClick={() => toggleAll(true)}>Alle auswählen</button>
                <button className="text-slate-500 font-medium hover:underline" onClick={() => toggleAll(false)}>Keine auswählen</button>
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto border border-slate-100 rounded-lg p-2">
                {visibleRecipients.map((r) => (
                  <label key={r.ticket_id} className="flex items-center gap-2 text-sm py-1.5 px-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={!!selected[r.ticket_id]} onChange={() => toggle(r.ticket_id)} />
                    <span className="font-medium text-slate-700">{r.name}</span>
                    <span className="text-slate-400">({r.email})</span>
                    <span className="ml-auto">
                      <Badge status={r.payment_status === 'confirmed' ? 'confirmed' : 'open'} custom={r.payment_status === 'confirmed' ? '✓ Bezahlt' : '⏳ Nicht bezahlt'} />
                    </span>
                  </label>
                ))}
                {visibleRecipients.length === 0 && <p className="text-center text-slate-400 py-4 text-sm">Keine Empfänger gefunden.</p>}
              </div>
            </>
          )}
        </div>
      )}

      <div className="card mb-4">
        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Betreff</label>
        <input
          className="input w-full mb-4"
          placeholder="z. B. Wichtige Infos zur BeachLAN 2026"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Inhalt</label>
        <ReactQuill theme="snow" value={body} onChange={setBody} modules={QUILL_MODULES} />
      </div>

      {result && <p className="text-sm text-emerald-700 mb-4">{result}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={openPreview} disabled={previewLoading}>
          {previewLoading ? 'Lade…' : 'Vorschau'}
        </Button>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 pb-3 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-lg text-ocean-800">Vorschau Newsletter</h3>
                <p className="text-xs text-slate-500 mt-0.5">{selectedIds.length} Empfänger ausgewählt</p>
              </div>
              <button onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 bg-slate-100">
              <iframe
                title="Newsletter-Vorschau"
                srcDoc={preview}
                className="w-full bg-white rounded-lg border border-slate-200"
                style={{ height: '55vh' }}
              />
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setPreview(null)}>Abbrechen</Button>
              <Button
                variant="primary"
                disabled={!selectedEvent || selectedIds.length === 0 || sending}
                onClick={send}
              >
                {sending ? 'Sende…' : `An ${selectedIds.length} Empfänger senden`}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="card mt-6">
        <h3 className="font-bold text-ocean-800 mb-3">Bisherige Newsletter</h3>
        {historyLoading ? (
          <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-6 w-6 border-2 border-ocean-400 border-t-transparent" /></div>
        ) : history.length === 0 ? (
          <p className="text-sm text-slate-400 py-2">Noch kein Newsletter versendet.</p>
        ) : (
          <div className="space-y-1">
            {history.map((n) => (
              <div
                key={n.id}
                className="flex flex-wrap items-center justify-between gap-2 py-2.5 px-2 rounded hover:bg-slate-50 border-b border-slate-50 last:border-0"
              >
                <button
                  onClick={() => viewEntry(n)}
                  className="flex-1 text-left cursor-pointer bg-transparent border-0 p-0 min-w-[200px]"
                >
                  <div>
                    <span className="font-medium text-slate-800">{n.subject}</span>
                    <span className="text-slate-400 ml-2">· {eventName(n.event_id)}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span>{fmtDate(n.sent_at)}</span>
                    <span>· {n.recipient_count} Empfänger</span>
                    {n.sender && <span>· {n.sender.firstname} {n.sender.lastname}</span>}
                  </div>
                </button>
                <Button
                  variant="danger" size="sm"
                  loading={deletingId === n.id}
                  onClick={() => deleteEntry(n)}
                >
                  Löschen
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewingEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setViewingEntry(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 pb-3 border-b border-slate-100">
              <div>
                <h3 className="font-bold text-lg text-ocean-800">{viewingEntry.subject}</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {eventName(viewingEntry.event_id)} · {fmtDate(viewingEntry.sent_at)} · {viewingEntry.recipient_count} Empfänger
                </p>
              </div>
              <button onClick={() => setViewingEntry(null)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">×</button>
            </div>

            <div className="flex-1 overflow-y-auto p-2 bg-slate-100">
              <iframe
                title="Newsletter-Ansicht"
                srcDoc={viewingEntry.html}
                className="w-full bg-white rounded-lg border border-slate-200"
                style={{ height: '55vh' }}
              />
            </div>

            <div className="p-4 border-t border-slate-100 flex justify-end">
              <Button variant="ghost" onClick={() => setViewingEntry(null)}>Schließen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
